import { Link } from "react-router-dom";
import type { Film } from "@/types/film";
import { typeLabels } from "@/lib/films";

interface DetailHeroProps {
  film: Film;
  onShare: () => void;
}

export default function DetailHero({ film, onShare }: DetailHeroProps) {
  return (
    <section className="relative overflow-hidden bg-primary-950">
      <div className="absolute inset-0">
        <img
          src={film.poster}
          alt=""
          aria-hidden="true"
          className="h-full w-full scale-110 object-cover object-top opacity-25 blur-2xl"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/85 to-primary-900/70" />
      </div>

      <div className="container-portal relative py-6 md:py-10">
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-background-300/75">
          <Link to="/" className="transition-colors hover:text-accent-300 cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <Link
            to={`/category/${film.type}`}
            className="transition-colors hover:text-accent-300 cursor-pointer"
          >
            {typeLabels[film.type]}
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-background-100">{film.title}</span>
        </nav>

        <div className="mt-5 flex flex-col gap-5 sm:flex-row sm:items-start md:gap-7">
          <div className="w-[140px] shrink-0 overflow-hidden rounded-lg border border-background-50/15 sm:w-[170px] md:w-[210px]">
            <img
              src={film.poster}
              alt={`${film.title} 海报`}
              title={`${film.title} 剧情介绍与影评`}
              className="aspect-[2/3] w-full object-cover object-top"
            />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded bg-accent-500 px-2 py-0.5 text-[11px] font-semibold text-background-50">
                {typeLabels[film.type]}
              </span>
              <span className="label-caps text-[10px] text-background-300/70">{film.en}</span>
              <span className="flex items-center gap-1 rounded bg-background-50/10 px-2 py-0.5 text-[11px] text-background-100">
                <i className="ri-calendar-line text-[11px]" />
                {film.year}
              </span>
            </div>

            <h1 className="mt-3 text-3xl font-black leading-tight tracking-tight text-background-50 md:text-[42px]">
              {film.title}
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-background-200/85">{film.intro}</p>

            <dl className="mt-5 grid grid-cols-1 gap-x-8 gap-y-2 text-[13px] sm:grid-cols-2">
              <div className="flex gap-2">
                <dt className="shrink-0 text-background-400/70">导演</dt>
                <dd className="text-background-100">{film.director}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="shrink-0 text-background-400/70">地区</dt>
                <dd className="text-background-100">{film.region}</dd>
              </div>
              <div className="flex min-w-0 gap-2">
                <dt className="shrink-0 text-background-400/70">主演</dt>
                <dd className="min-w-0 truncate text-background-100">{film.cast.join(" / ")}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="shrink-0 text-background-400/70">片长</dt>
                <dd className="text-background-100">{film.length}</dd>
              </div>
            </dl>

            <div className="mt-5 flex flex-wrap items-center gap-2">
              {film.genres.map((genre) => (
                <span
                  key={genre}
                  className="rounded border border-background-50/20 px-2 py-0.5 text-[11px] text-background-200"
                >
                  {genre}
                </span>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <div className="flex items-baseline gap-1.5">
                <i className="ri-star-fill text-accent-400" />
                <span className="font-label text-3xl leading-none text-background-50">
                  {film.rating.toFixed(1)}
                </span>
                <span className="text-[11px] text-background-400/70">/ 10</span>
              </div>
              <span className="h-8 w-px bg-background-50/15" />
              <a
                href="#reviews"
                className="inline-flex items-center gap-1.5 rounded-md bg-accent-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-chat-quote-line" />
                阅读影评
              </a>
              <button
                type="button"
                onClick={onShare}
                className="inline-flex items-center gap-1.5 rounded-md border border-background-50/25 px-4 py-2.5 text-sm text-background-100 transition-colors hover:border-accent-400 hover:text-accent-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-share-forward-line" />
                分享
              </button>
              <Link
                to={`/category/${film.type}`}
                className="inline-flex items-center gap-1.5 px-1 py-2.5 text-sm text-background-300/80 transition-colors hover:text-background-50 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line" />
                返回{typeLabels[film.type]}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}